import _ from 'lodash';
import validator from 'validator';

import Service from '@/lib/App';
import { ApprovalFlowViewModel } from '@/lib/PlannerDTOs';

// Creator of overlapping experiments are whitelisted to approve current experiment
// regardless of their access level
export const getWhitelistedApprovers = (
  approvalFlow: ApprovalFlowViewModel[],
) => {
  const processEmails = (emails: string): string[] => {
    return emails
      .split(',')
      .map((email) => email.trim())
      .filter((email) => validator.isEmail(email));
  };

  return _.uniq(
    approvalFlow
      .filter(({ resource }) => !!resource)
      .flatMap(({ email }) => processEmails(email)),
  );
};

export const isWhitelistedApprover = (
  approvalFlow: ApprovalFlowViewModel[],
) => {
  const userInfo = Service.cache.getUserInfo();
  if (!userInfo || !userInfo.email) {
    return false;
  }

  return getWhitelistedApprovers(approvalFlow).includes(userInfo.email);
};

type TargetByCountryValues = string;
type TargetByCityValues = string[];
type TargetByRegionValues = string[];

export type TargetValues =
  | TargetByCountryValues
  | TargetByCityValues
  | TargetByRegionValues
  | null;

const isTargetByCountryValues = (
  value: TargetValues,
): value is TargetByCountryValues => {
  return (
    value &&
    typeof value === 'string' &&
    value.startsWith('[') &&
    value.endsWith(']')
  );
};

const isTargetByCityValues = (
  value: TargetValues,
): value is TargetByCityValues => {
  return (
    Array.isArray(value) &&
    value.length &&
    value.every((v) => Boolean(parseInt(v)))
  );
};

const isTargetByRegionValues = (
  value: TargetValues,
): value is TargetByRegionValues => {
  return (
    Array.isArray(value) &&
    value.length &&
    value.every((v) => typeof v === 'string')
  );
};

export const transformPopulationTargetValueToId = (value: TargetValues) => {
  if (isTargetByCityValues(value)) {
    return value.join(',');
  }

  let countryCodes = [];
  if (isTargetByCountryValues(value)) {
    // string: '[MY, SG]'
    countryCodes = value
      .slice(1, -1)
      .split(',')
      .map((code) => code.trim());
  } else if (isTargetByRegionValues(value)) {
    // array: ['MY', 'SG']
    countryCodes = value;
  } else {
    return null;
  }

  const countries = Service.cache!.getAllCountries();
  const countryIds = countryCodes
    .map((code) => {
      const country = countries.find((c) => c.Code === code);
      return country?.ID;
    })
    .filter(Boolean);

  return countryIds.join(',');
};
